import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useVault } from "./useVault";
import { useSession } from "../stores/session";

const FOLDER_THUMBS_STALE_MS = 60_000;

/**
 * Fetch every thumbnail in `folderPath`'s `.thumbnails` sidecar in
 * one vault call. Shared across every PhotoTile in the folder via
 * the React Query cache — N tiles = one vault read, not N. That also
 * sidesteps the wasm handle-registry contention that makes
 * concurrent per-file getThumbnail calls fail for all but one of
 * them (see doc comment on the worker's getFolderThumbnails).
 */
export function useFolderThumbnails(
  folderPath: string,
): Record<string, Uint8Array> {
  const vault = useVault();
  const session = useSession((s) => s.session);
  const q = useQuery({
    enabled: !!session,
    queryKey: ["folder-thumbnails", session?.handle, folderPath] as const,
    queryFn: async () => {
      if (!session) return {} as Record<string, Uint8Array>;
      return vault.getFolderThumbnails(session.handle, folderPath);
    },
    staleTime: FOLDER_THUMBS_STALE_MS,
  });
  return q.data ?? {};
}

/**
 * Invalidate the folder-thumbnails query for one folder. Call this
 * after an upload completes so the grid picks up the newly-added
 * thumbnails instead of serving stale data until staleTime elapses.
 */
export function useInvalidateFolderThumbnails(): (folderPath: string) => void {
  const qc = useQueryClient();
  const session = useSession((s) => s.session);
  return (folderPath) => {
    if (!session) return;
    void qc.invalidateQueries({
      queryKey: ["folder-thumbnails", session.handle, folderPath],
    });
  };
}

/**
 * Returns a blob-URL for the thumbnail of `fileName` in `folderPath`,
 * or null if no thumbnail exists for that entry. Both image uploads
 * and video uploads write a WebP into the per-folder sidecar, so we
 * gate on `image/` or `video/` and let the sidecar lookup decide
 * whether bytes are actually present (a missing entry just returns
 * null and the caller falls back to the file-type badge).
 *
 * Revokes the URL on unmount and on lock.
 */
export function useThumbnail(
  folderPath: string,
  fileName: string,
  mime?: string,
): string | null {
  const hasThumb =
    mime?.startsWith("image/") || mime?.startsWith("video/") || false;
  const thumbnails = useFolderThumbnails(folderPath);
  const bytes = hasThumb ? (thumbnails[fileName] ?? null) : null;

  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!bytes) {
      setUrl(null);
      return;
    }
    const blobUrl = URL.createObjectURL(
      new Blob([new Uint8Array(bytes)], { type: "image/webp" }),
    );
    setUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [bytes]);

  return url;
}
